import * as RadixPopover from '@radix-ui/react-popover';
import {
  forwardRef,
  memo,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { type Skill } from '../../skills';
import { getPeekingListMaxHeight } from './getPeekingListMaxHeight';
import { SkillSuggestionItem } from './SkillSuggestionItem';

const SKILL_LIST_MAX_HEIGHT_PX = 320;

// Returning true from onKeyDown tells TipTap Suggestion the key was handled (see plugin source).
// Do not return true for Escape — the plugin only calls dispatchExit when Escape's onKeyDown returns false.
export type SkillSuggestionPopupRef = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export type SkillSuggestionPopupProps = {
  /**
   * Composer frame. The list anchors here, not at the caret, so it sits on the top edge of the
   * input. A caret anchor lands inside the field and covers the canvas chat input.
   */
  anchorRef: RefObject<HTMLElement>;
  command: (item: Skill) => void;
  /** Dismiss the TipTap suggestion; required because Radix DismissableLayer handles Escape in document capture before the editor. */
  dismissFromPortal: () => void;
  items: Skill[];
  /** Opens the host's edit-skill modal; when omitted, no pencil is shown. */
  onEditSkill?: (skill: Skill) => void;
  /** When omitted, no + button is shown. */
  onCreateSkill?: () => void;
  /** Whether the current user can edit a given skill. */
  canEditSkill?: (skill: Skill) => boolean;
  className?: string; // NOSONAR -- used by the inner component; S6774 misses memo(forwardRef)
  /** Preferred side of the anchor; Radix still flips it when there isn't room. */
  side?: 'bottom' | 'top'; // NOSONAR -- used by the inner component; S6774 misses memo(forwardRef)
};

export const SkillSuggestionPopup = memo(
  forwardRef<SkillSuggestionPopupRef, SkillSuggestionPopupProps>(
    (
      {
        anchorRef,
        canEditSkill,
        className,
        command,
        dismissFromPortal,
        items,
        onCreateSkill,
        onEditSkill,
        side = 'top',
      },
      ref,
    ) => {
      const [selectedIndex, setSelectedIndex] = useState(0);
      const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
      const [maxHeight, setMaxHeight] = useState(SKILL_LIST_MAX_HEIGHT_PX);
      const itemsVersion = useMemo(() => items.map((i) => i.slashCommand).join('\0'), [items]);

      // A list that scrolls ends halfway through a row, so it doesn't look complete when it isn't.
      // The content mounts a commit after the portal does, hence the node in state rather than a ref.
      // itemsVersion isn't read inside; it re-measures when the filtered list changes.
      useIsomorphicLayoutEffect(() => {
        if (!contentNode) {
          return;
        }

        const { top: contentTop } = contentNode.getBoundingClientRect();
        const rows = Array.from(contentNode.querySelectorAll('[role="option"]'), (row) => {
          const { bottom, top } = row.getBoundingClientRect();

          return { bottom: bottom - contentTop + contentNode.scrollTop, top: top - contentTop + contentNode.scrollTop };
        });

        setMaxHeight(getPeekingListMaxHeight(rows, SKILL_LIST_MAX_HEIGHT_PX) ?? SKILL_LIST_MAX_HEIGHT_PX);
      }, [contentNode, itemsVersion]);

      const activeDescendantId = items[selectedIndex] ? `skill-suggestion-option-${selectedIndex}` : undefined;
      const selected = items[selectedIndex];

      // biome-ignore lint/correctness/useExhaustiveDependencies: run when the filtered list identity changes
      useEffect(() => {
        setSelectedIndex(0);
      }, [itemsVersion]);

      const selectItem = useCallback(
        (index: number) => {
          const item = items[index];

          if (item) {
            command(item);
          }
        },
        [items, command],
      );

      // Editing opens a host modal that can rename the skill (slash command included). Tear down the
      // suggestion session first so a subsequent Enter can't insert the now-stale in-memory entry.
      const handleEditSkill = useCallback(
        (skill: Skill) => {
          dismissFromPortal();
          onEditSkill?.(skill);
        },
        [dismissFromPortal, onEditSkill],
      );

      const handleCreateSkill = useCallback(() => {
        dismissFromPortal();
        onCreateSkill?.();
      }, [dismissFromPortal, onCreateSkill]);

      const handleOpenAutoFocus = useCallback((event: Event) => event.preventDefault(), []);

      const handleEscapeKeyDown = useCallback(
        (event: KeyboardEvent) => {
          dismissFromPortal();
          event.preventDefault();
        },
        [dismissFromPortal],
      );

      useImperativeHandle(
        ref,
        () => ({
          onKeyDown: (event: KeyboardEvent) => {
            if (items.length === 0) {
              return false;
            }

            if (event.key === 'ArrowDown') {
              setSelectedIndex((prev) => (prev + 1) % items.length);

              return true;
            }

            if (event.key === 'ArrowUp') {
              setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);

              return true;
            }

            if (event.key === 'Enter') {
              selectItem(selectedIndex);

              return true;
            }

            if (event.key === 'Escape') {
              // False: let @tiptap/suggestion run dispatchExit when the editor receives the key; Radix
              // onEscapeKeyDown + dismissFromPortal handles focus-in-popover. useEscShortcut uses
              // window capture (before Radix) for hadChatSuggestionRef.
              return false;
            }

            return false;
          },
        }),
        [items.length, selectedIndex, selectItem],
      );

      if (items.length === 0) {
        return null;
      }

      return (
        <RadixPopover.Root open>
          <RadixPopover.Anchor virtualRef={anchorRef} />
          <RadixPopover.Portal>
            <RadixPopover.Content
              align="start"
              className={cn(
                'z-portal flex min-w-60 max-w-[360px] radix-side-bottom:animate-slide-up radix-side-top:animate-slide-down flex-col gap-1 overflow-y-auto rounded-8 ads:border-ads-gray-5 bg-surface-1 p-1.5 shadow-popover outline-none dark:border dark:border-gray-5',
                className,
              )}
              collisionPadding={8}
              data-testid="SKILL_SUGGESTION_POPUP"
              onEscapeKeyDown={handleEscapeKeyDown}
              onOpenAutoFocus={handleOpenAutoFocus}
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              onPointerDownOutside={() => dismissFromPortal()}
              ref={setContentNode}
              side={side}
              sideOffset={8}
              style={{ maxHeight }}
            >
              <div className="flex items-center justify-between gap-2 border-b ads:border-b-default border-b-gray-4 p-2">
                <span aria-hidden className="font-medium ads:text-metadata text-12 text-gray-9">
                  Skills
                </span>
                {onCreateSkill && (
                  <Button
                    appearance="ghost"
                    color="gray"
                    data-testid="SKILL_PICKER_CREATE_BUTTON"
                    onClick={handleCreateSkill}
                    prefix={<PlusIcon size="xs" />}
                    size="small"
                    type="button"
                  >
                    Add
                  </Button>
                )}
              </div>
              <div
                aria-activedescendant={activeDescendantId}
                aria-label="Skill suggestions"
                className="flex flex-col gap-1 p-2"
                role="listbox"
                tabIndex={-1}
              >
                {items.map((item, index) => (
                  <SkillSuggestionItem
                    canEdit={canEditSkill?.(item) ?? false}
                    id={`skill-suggestion-option-${index}`}
                    index={index}
                    item={item}
                    key={item.slashCommand}
                    onEdit={onEditSkill ? handleEditSkill : undefined}
                    onMouseEnter={setSelectedIndex}
                    onSelect={selectItem}
                    selected={index === selectedIndex}
                  />
                ))}
              </div>
              <div aria-live="polite" className="sr-only">
                {selected
                  ? `${selected.name}${selected.description ? `, ${selected.description}` : ''}, ${selectedIndex + 1} of ${items.length}`
                  : null}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      );
    },
  ),
);

SkillSuggestionPopup.displayName = 'SkillSuggestionPopup';
